import { useState, useEffect, useCallback, useRef } from 'react';

interface UseInfiniteScrollOptions {
  loadMore: () => Promise<void>;
  hasMore: boolean;
  loading: boolean;
  rootMargin?: string;
  threshold?: number;
}

interface UseInfiniteScrollReturn {
  sentinelRef: React.RefObject<HTMLDivElement>;
  loadingRef: React.RefObject<HTMLDivElement>;
}

export const useInfiniteScroll = ({
  loadMore,
  hasMore,
  loading,
  rootMargin = '200px',
  threshold = 0.1
}: UseInfiniteScrollOptions): UseInfiniteScrollReturn => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    setIsIntersecting(entry.isIntersecting);
  }, []);

  // Set up intersection observer for the sentinel element
  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    let mounted = true; // Track if component is mounted
    
    const setupObserver = () => {
      if (!mounted) return; // Don't setup if component unmounted
      
      const sentinel = sentinelRef.current;
      if (!sentinel) return; // Don't retry, just wait for next effect trigger

      observer = new IntersectionObserver(handleIntersection, {
        rootMargin,
        threshold
      });

      observer.observe(sentinel);
    };

    // Only setup observer if we should have more results
    if (hasMore && !loading) {
      setupObserver();
    }

    return () => {
      mounted = false; // Mark as unmounted
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    };
  }, [handleIntersection, rootMargin, threshold, hasMore, loading]);

  // Trigger load more when sentinel is intersecting
  useEffect(() => {
    if (isIntersecting && hasMore && !loading) {
      loadMore();
    }
  }, [isIntersecting, hasMore, loading, loadMore]);

  return {
    sentinelRef,
    loadingRef
  };
};

export default useInfiniteScroll;